/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Events logic module.
 * Handles event operations including fetching events, business events,
 * attendance management, and event data aggregation for the events page.
 * @module logic/events
 */

import {
  CONTENT_TYPE_JSON,
  HTTP_BAD_REQUEST,
  HTTP_NOT_FOUND,
  TOKEN_EXPIRED_MESSAGE,
  fetchUserRoleData,
  getAuthJsonHeaders,
  handleExpiredToken,
  showEventCreatedSuccess,
} from "../../utils/eventApi.js";
import {
  ADMIN_USER_TYPE,
  isBusinessStyleEventVisible,
  isRegistrationEventVisible,
} from "../../utils/eventDateFilters.js";
import {
  canViewPastEvents,
  filterNonCancelledEvents,
  sortEventsFuturesFirst,
} from "../../utils/pastEventsAccess.js";
import { getUser } from "../user";
import { getVirtualEventsForEventsList } from "../virtualEvents";
import { getBreakfastEventsForEventsList } from "../breakfastEvents";

export { showEventCreatedSuccess };

/**
 * Fetches and sets user role/permission data.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} changePermissions - Callback to update permissions state
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 */
export const getUserRoleData = fetchUserRoleData;

/**
 * Fetches events available to a user based on membership type and date filters.
 * @async
 * @param {string} userId - User identifier
 * @param {Object} user - User object with tipoDeUsuario property
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of available event objects
 */
export const getEvents = async (userId, user, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}event/user/${userId}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const refresh = await handleExpiredToken(navigate, () =>
        getEvents(userId, user, navigate),
      );
      if (refresh.redirected) {
        return [];
      }
      return refresh.result;
    }
    if (Array.isArray(data)) {
      const currentDate = new Date();
      return data.filter((event) =>
        isRegistrationEventVisible(event, user, currentDate),
      );
    }
    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Fetches registration events for management screens.
 * Eligible users (admin / Reports / Attendance) receive past non-cancelled events.
 * @async
 * @param {string} userId - User identifier
 * @param {Object} user - User object with tipoDeUsuario property
 * @param {Function} navigate - React Router navigation function
 * @param {object|null|undefined} [permissions]
 * @returns {Promise<Array>} Array of available event objects
 */
export const getEventsForManagement = async (
  userId,
  user,
  navigate,
  permissions,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}event/user/${userId}/management`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const refresh = await handleExpiredToken(navigate, () =>
        getEventsForManagement(userId, user, navigate, permissions),
      );
      if (refresh.redirected) {
        return [];
      }
      return refresh.result;
    }
    if (Array.isArray(data)) {
      const includePast = canViewPastEvents(user, permissions);
      const nonCancelled = filterNonCancelledEvents(data);
      if (includePast) {
        return sortEventsFuturesFirst(nonCancelled);
      }
      const currentDate = new Date();
      return sortEventsFuturesFirst(
        nonCancelled.filter((event) => {
          const eventDate = new Date(event.fechaEvento);
          return eventDate >= currentDate;
        }),
      );
    }
    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Fetches all active events from the API.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {{ includePast?: boolean, userId?: string }} [options]
 * @returns {Promise<Array>} Array of active event objects
 */
export const getActiveEvents = async (navigate, options = {}) => {
  const { includePast = false, userId } = options;
  const requestOptions = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const params = new URLSearchParams();
    if (includePast) {
      params.set("includePast", "true");
      if (userId) {
        params.set("userId", userId);
      }
    }
    const query = params.toString();
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}event/active${query ? `?${query}` : ""}`,
      requestOptions,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const refresh = await handleExpiredToken(navigate, () =>
        getActiveEvents(navigate, options),
      );
      if (refresh.redirected) {
        return [];
      }
      return refresh.result;
    }
    if (Array.isArray(data)) {
      return filterNonCancelledEvents(data);
    }
    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Registration events (tipoEvento REGISTRATION_EVENT) for the manage-events screen; same filters as {@link getEvents}.
 * @async
 * @param {string} userId
 * @param {Object} user
 * @param {Function} navigate
 * @param {object|null|undefined} [permissions]
 * @returns {Promise<Array>}
 */
export const getRegistrationEventsForManagement = async (
  userId,
  user,
  navigate,
  permissions,
) => {
  const list = await getEventsForManagement(
    userId,
    user,
    navigate,
    permissions,
  );
  return list || [];
};

/**
 * Loads permissions, user, and registration-only events for {@link ManageEvents} page.
 * @async
 * @param {string} userId
 * @param {Function} changePermissions
 * @param {Function} navigate
 * @param {Function} setUser
 * @returns {Promise<Array>}
 */
export const getManagedRegistrationEventsData = async (
  userId,
  changePermissions,
  navigate,
  setUser,
) => {
  try {
    const permissions = await getUserRoleData(
      userId,
      changePermissions,
      navigate,
    );
    const userData = await getUser(userId, navigate);
    setUser(userData);
    const events = await getRegistrationEventsForManagement(
      userId,
      userData,
      navigate,
      permissions,
    );
    return events || [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Fetches business events available to a user based on membership type and date filters.
 * @async
 * @param {string} userId - User identifier
 * @param {Object} user - User object with tipoDeUsuario property
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of available business event objects
 */
export const getBusinessEvents = async (userId, user, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/user/${userId}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const refresh = await handleExpiredToken(navigate, () =>
        getBusinessEvents(userId, user, navigate),
      );
      if (refresh.redirected) {
        return [];
      }
      return refresh.result;
    }
    if (Array.isArray(data)) {
      const currentDate = new Date();
      const events =
        user?.tipoDeUsuario !== ADMIN_USER_TYPE
          ? data.filter((event) => event.activo === true)
          : data;

      return events.filter((event) =>
        isBusinessStyleEventVisible(event, user, currentDate),
      );
    }
    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * List B2B events visible to the user (lightweight).
 * Backend applies filtering (activo, fechaFinEvento, fechaDisponibleSocios/NoSocios, user type).
 * Does not include userAppointments, disponibilidadEjecutivosRegistro, or duration hints — use {@link getB2BEventForUser} on the detail screen.
 */
export const getB2BEvents = async (userId, user, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/user/${userId}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const refresh = await handleExpiredToken(navigate, () =>
        getB2BEvents(userId, user, navigate),
      );
      if (refresh.redirected) {
        return [];
      }
      return refresh.result;
    }
    if (
      response.status === HTTP_BAD_REQUEST ||
      response.status === HTTP_NOT_FOUND
    ) {
      return [];
    }
    if (Array.isArray(data)) {
      const currentDate = new Date();
      return data.filter((event) =>
        isBusinessStyleEventVisible(event, user, currentDate),
      );
    }
    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Cancels a user's attendance for an event.
 * @async
 * @param {Object} selectedEvent - Event object with _id property
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|null>} API response data, or null when redirected after auth failure
 * @throws {Error} If cancellation fails
 */
export const cancelAttendance = async (selectedEvent, navigate) => {
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      idUsuario: userId,
      idEvento: selectedEvent._id,
      estatus: true,
    }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/attendance/cancel`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const refresh = await handleExpiredToken(navigate, () =>
        cancelAttendance(selectedEvent, navigate),
      );
      if (refresh.redirected) {
        return null;
      }
      return refresh.result;
    }

    return data;
  } catch (error) {
    console.error("Error cancelling attendance:", error);
    throw error;
  }
};

/**
 * Updates local state after attendance cancellation and calls the cancel API.
 * @async
 * @param {Array} eventsData - Current events array
 * @param {Object} selectedEvent - Event being cancelled
 * @param {Function} setEventsData - State setter for events
 * @param {Function} setSelectedEvent - State setter for selected event
 * @param {Function} setCancelAttendanceModalOpen - State setter for modal visibility
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 */
export const updateAttendanceCancelledStatus = async (
  eventsData,
  selectedEvent,
  setEventsData,
  setSelectedEvent,
  setCancelAttendanceModalOpen,
  navigate,
) => {
  const updatedEventsData = eventsData.map((event) => {
    if (event._id === selectedEvent._id) {
      return {
        ...event,
        isUserRegistered: false,
      };
    }
    return event;
  });

  try {
    await cancelAttendance(selectedEvent, navigate);
    setEventsData(updatedEventsData);
  } catch (error) {
    console.error("Error updating cancelled status:", error);
  } finally {
    setSelectedEvent(null);
    setCancelAttendanceModalOpen(false);
  }
};

/**
 * Fetches initial page data including user roles, user data, and combined events.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} changePermissions - Callback to update permissions state
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setUser - State setter for user data
 * @returns {Promise<Array>} Sorted array of all events (registration + business)
 */
export const getData = async (userId, changePermissions, navigate, setUser) => {
  try {
    await getUserRoleData(userId, changePermissions, navigate);
    const userData = await getUser(userId, navigate);
    setUser(userData);
    const registrationEvents = await getEvents(userId, userData, navigate);
    const businessEvents = await getBusinessEvents(userId, userData, navigate);
    const b2bEvents = await getB2BEvents(userId, userData, navigate);
    const breakfastEvents = await getBreakfastEventsForEventsList(
      userId,
      userData,
      navigate,
    );
    const virtualEvents = await getVirtualEventsForEventsList(
      userId,
      userData,
      navigate,
    );

    const events = [
      ...(registrationEvents || []),
      ...(businessEvents || []),
      ...b2bEvents,
      ...virtualEvents,
      ...breakfastEvents,
    ];
    return events.sort((a, b) => {
      const dateA = new Date(a.fechaEvento || a.fechaInicioEvento);
      const dateB = new Date(b.fechaEvento || b.fechaInicioEvento);
      return dateA - dateB;
    });
  } catch (error) {
    console.error(error);
    return [];
  }
};
